'use client';

import { useRouter } from 'next/navigation';
import { signIn, signOut, useSession } from 'next-auth/react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Wordmark } from '@/lib/Wordmark';
import styles from '../styles/Home.module.css';

type Meeting = { roomName: string; url: string; title: string };

/** Pull a room id out of a pasted link or a raw code. */
function parseCode(input: string): string | null {
  const v = input.trim();
  if (!v) return null;
  try {
    if (v.includes('/')) {
      const path = v.startsWith('http') ? new URL(v).pathname : v;
      const seg = path.split('/').filter(Boolean).pop();
      return seg ? decodeURIComponent(seg) : null;
    }
  } catch {
    /* fall through to treat as a bare code */
  }
  return v;
}

export default function Page() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const signedIn = !!session?.user;
  const [creating, setCreating] = useState(false);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [code, setCode] = useState('');

  const newMeeting = async () => {
    setCreating(true);
    try {
      const res = await fetch('/api/meetings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'TiniMeet meeting' }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data: Meeting = await res.json();
      setMeeting(data);
    } catch (e) {
      toast.error("Couldn't create a meeting. Check the server config.");
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    if (!meeting) return;
    try {
      await navigator.clipboard.writeText(meeting.url);
      toast.success('Link copied — share it around!');
    } catch {
      toast('Copy failed — select the link and copy manually.');
    }
  };

  const join = (e: React.FormEvent) => {
    e.preventDefault();
    const room = parseCode(code);
    if (!room) {
      toast('Paste a meeting link or code first.');
      return;
    }
    router.push(`/rooms/${encodeURIComponent(room)}`);
  };

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <Wordmark size={30} />
        {signedIn ? (
          <span className={styles.account}>
            <span className={styles.accountEmail}>{session!.user!.email}</span>
            <button className="tm-btn tm-btn--ghost" onClick={() => signOut()}>
              Sign out
            </button>
          </span>
        ) : (
          <span className={styles.beta}>beta</span>
        )}
      </header>

      <main className={styles.hero}>
        <section className={styles.pitch}>
          <h1 className={styles.headline}>
            A tiny meeting room for <span className={styles.penUnderline}>people & AI</span>.
          </h1>
          <p className={styles.sub}>
            Spin up a link, share it, and hop on. Humans and voice agents share the same
            room — no accounts, no fuss.
          </p>

          {!meeting ? (
            <div className={styles.actions}>
              {signedIn ? (
                <button
                  className={`tm-btn tm-btn--primary ${styles.newBtn}`}
                  onClick={newMeeting}
                  disabled={creating}
                >
                  {creating ? 'Sketching a room…' : '＋ New meeting'}
                </button>
              ) : (
                <button
                  className={`tm-btn tm-btn--primary ${styles.newBtn}`}
                  onClick={() => signIn('google')}
                  disabled={status === 'loading'}
                >
                  <span className={styles.gLogo} aria-hidden="true">
                    G
                  </span>
                  Sign in to start a meeting
                </button>
              )}
              {!signedIn && (
                <p className="tm-note">only @tinisoft.in accounts can create meetings.</p>
              )}

              <div className={styles.or}>
                <span>or</span>
              </div>

              <form className={styles.joinRow} onSubmit={join}>
                <input
                  className="tm-input"
                  placeholder="Paste a link or code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  aria-label="Meeting link or code"
                />
                <button className="tm-btn" type="submit">
                  Join →
                </button>
              </form>
              <p className="tm-note">↳ got a link from someone? Drop it in and jump straight in — no account needed.</p>
            </div>
          ) : (
            <div className={`sketch sketch--c ${styles.shareCard}`}>
              <p className={styles.shareTitle}>Your room is ready ✎</p>
              <p className={styles.shareHint}>Share this link — anyone who opens it can join.</p>
              <div className={`sketch sketch--b ${styles.linkBox}`}>{meeting.url}</div>
              <div className={styles.shareBtns}>
                <button className="tm-btn" onClick={copyLink}>
                  ⧉ Copy link
                </button>
                <button
                  className="tm-btn tm-btn--primary"
                  onClick={() => router.push(`/rooms/${encodeURIComponent(meeting.roomName)}`)}
                >
                  Start now →
                </button>
                <button className="tm-btn tm-btn--ghost" onClick={() => setMeeting(null)}>
                  ← Back
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Signature illustration: two wireframe tiles = a human + an agent in a call */}
        <section className={styles.illus} aria-hidden="true">
          <div className={styles.callFrame}>
            <div className={`${styles.tile} ${styles.tileYou}`}>
              <span className={styles.tileTag}>You</span>
            </div>
            <div className={`${styles.tile} ${styles.tileAi}`}>
              <span className={styles.aiDot} />
              <span className={styles.tileTag}>AI agent</span>
            </div>
            <div className={styles.callBar}>
              <span className={styles.ctrl} />
              <span className={styles.ctrl} />
              <span className={`${styles.ctrl} ${styles.ctrlEnd}`} />
            </div>
          </div>
          <p className="tm-note" style={{ textAlign: 'center', marginTop: '0.75rem' }}>
            camera off? you&apos;re a placeholder box — turn it on and the sketch comes alive.
          </p>
        </section>
      </main>

      <footer className={styles.footer}>
        <span>TiniMeet</span>
        <span className={styles.dot}>·</span>
        <span>runs on your own LiveKit server</span>
      </footer>
    </div>
  );
}
